import React, {useMemo, useState} from 'react';
import {useOhdMetrics} from '../hooks/useOhdMetrics';
import ProgramParticipationCard from './ProgramParticipationCard';
import ScreeningModalityCard from './ScreeningModalityCard';
import RiskAssessmentCard from './RiskAssessmentCard';
import SmartTestingCard from './SmartTestingCard';
import SmartTestCardsSection from './SmartTestCardsSection';
import BloodPressureTable from './BloodPressureTable';
import HbA1cTable from './HbA1cTable';
import {useBioMatchDailySeries, useBioMatchReporting} from '@/modules/biomatch/hooks/useBioMatchReporting';
import BioMatchProgramCard from '@/modules/biomatch/components/BioMatchProgramCard';
import {ACCENT_LAB_TREND, colorForProgram} from './chart-colors';

interface Props {
    /** OHD-side organization id (from tenant features.ohd_organization_id). */
    ohdOrganizationId?: number;
    /** MLD-side organization id, used for BioMatch enrollment reporting. */
    mldOrganizationId?: number;
}

function campaignYear(starts_at?: string, name?: string): string | number {
    if (starts_at) {
        const y = new Date(starts_at).getUTCFullYear();
        if (!Number.isNaN(y)) return y;
    }
    if (name) {
        const match = name.match(/(20\d{2})/);
        if (match) return match[1];
    }
    return '—';
}

/** Shift an ISO date string by `months` whole months. Negative = earlier. */
function shiftMonths(iso: string, months: number): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    d.setUTCMonth(d.getUTCMonth() + months);
    return d.toISOString();
}

const EmployeeHealthDashboard: React.FC<Props> = ({ohdOrganizationId, mldOrganizationId}) => {
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | undefined>(undefined);

    const metricsQuery = useOhdMetrics(ohdOrganizationId, selectedCampaignId);
    const metrics = metricsQuery.data;
    const reportingQuery = useBioMatchReporting(mldOrganizationId);
    const programRows = reportingQuery.data ?? [];
    const dailySeriesQuery = useBioMatchDailySeries(mldOrganizationId, 30);
    const seriesByRec = useMemo(() => {
        const m = new Map<number, {day: string; enrollments: number}[]>();
        for (const p of dailySeriesQuery.data ?? []) {
            const list = m.get(p.recommendation_id) ?? [];
            list.push({day: p.day, enrollments: p.enrollments});
            m.set(p.recommendation_id, list);
        }
        return m;
    }, [dailySeriesQuery.data]);

    const priorCampaignId = useMemo(() => {
        if (!metrics) return undefined;
        const list = metrics.campaign_list ?? [];
        const currentIndex = list.findIndex((c) => c.id === metrics.campaign_id);
        if (currentIndex < 0) return undefined;
        return list[currentIndex + 1]?.id;
    }, [metrics]);

    const priorQuery = useOhdMetrics(ohdOrganizationId, priorCampaignId);
    const prior = priorQuery.data;

    if (!ohdOrganizationId) {
        return (
            <div className="mx-auto max-w-[960px] px-6 py-10">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
                    <div className="text-[16px] font-semibold">OHD organization ID not configured</div>
                    <p className="mt-2 text-[14px]">
                        Set <code className="rounded bg-amber-100 px-1">ohd_organization_id</code> in the
                        organization&apos;s feature flags (Super Admin → organization settings) to enable the OHD
                        dashboard.
                    </p>
                </div>
            </div>
        );
    }

    if (metricsQuery.isLoading) {
        return (
            <div className="mx-auto max-w-[960px] px-6 py-10 text-[14px] text-mld-muted-text">
                Loading OHD metrics…
            </div>
        );
    }

    if (metricsQuery.isError || !metrics) {
        return (
            <div className="mx-auto max-w-[960px] px-6 py-10">
                <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900">
                    <div className="text-[16px] font-semibold">Unable to load OHD metrics</div>
                    <p className="mt-2 text-[14px]">
                        {metricsQuery.error instanceof Error ? metricsQuery.error.message : 'Unknown error.'}
                    </p>
                </div>
            </div>
        );
    }

    const currentYear = campaignYear(metrics.campaign_starts_at ?? undefined, metrics.campaign_name);
    const priorYear = prior ? campaignYear(prior.campaign_starts_at ?? undefined, prior.campaign_name) : null;

    return (
        <div className="mx-auto max-w-[960px] space-y-8 px-6 py-10">
            {/* Title + campaign selector */}
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[22px] font-bold text-mld-text-primary">
                        {currentYear} Employee Health Summary
                    </h1>
                    <div className="mt-2 h-[3px] w-20 rounded-full" style={{backgroundColor: ACCENT_LAB_TREND}} />
                </div>
                <div className="flex flex-col items-end gap-1">
                    <label className="text-[11px] uppercase tracking-wide text-mld-muted-text" htmlFor="ohd-campaign">
                        Campaign
                    </label>
                    <select
                        id="ohd-campaign"
                        className="rounded-md border border-gray-300 bg-mld-menu-background pl-3 pr-8 py-1.5 text-[13px] text-mld-menu-text"
                        value={selectedCampaignId ?? metrics.campaign_id}
                        onChange={(e) => setSelectedCampaignId(Number(e.target.value))}>
                        {metrics.campaign_list.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Top row: Participation + Screening Modality */}
            <div className="grid grid-cols-2 gap-5">
                <ProgramParticipationCard
                    participationRate={metrics.participation.participation_rate}
                    totalEligible={metrics.participation.total_eligible_users}
                    totalResults={metrics.result_set_counts.total_results}
                />
                <ScreeningModalityCard counts={metrics.result_set_counts} />
            </div>

            {/* Risk Assessment */}
            <RiskAssessmentCard
                risk={metrics.risk_metrics}
                participation={metrics.participation}
                bp={metrics.blood_pressure_breakdown}
                hba1c={metrics.hba1c_breakdown}
            />

            {/* Smart Testing — sources from imported WooCommerce orders
                for the org. Window = campaign start − 2 months → campaign
                end, because employees routinely pre-order kits before the
                campaign formally starts. */}
            <SmartTestingCard
                mldOrganizationId={mldOrganizationId}
                startDate={metrics.campaign_starts_at ? shiftMonths(metrics.campaign_starts_at, -2) : undefined}
                endDate={metrics.campaign_ends_at ?? undefined}
            />

            {/* Per-panel completion cards (Lipid, CMP, HbA1c, PSA-m, TSH-f,
                Cotinine). Pulls live from results joined to assay_catalog.
                Cotinine renders in placeholder state until LabCorp results
                are integrated — see CLAUDE.md. */}
            <SmartTestCardsSection
                mldOrganizationId={mldOrganizationId}
                campaignId={selectedCampaignId ?? metrics.campaign_id}
            />

            {/* BioMatch program cards — real enrollment counts from
                biomatch_enrollments. Only render when this org actually
                has BioMatch enrollment rows; otherwise the panel
                completion cards above (SmartTestCardsSection) carry
                the page on their own. The legacy "A1C Reflex / Cardiac
                Control / Weight Management / Hormone Health"
                placeholders were removed — those slots are now real
                panel-completion data per Christy's spec. */}
            {mldOrganizationId && programRows.length > 0 && (
                <div className="grid grid-cols-2 gap-5">
                    {programRows.map((row) => (
                        <BioMatchProgramCard
                            key={row.recommendation_id}
                            row={row}
                            color={colorForProgram(row.recommendation_name, row.risk_tier)}
                            dailySeries={seriesByRec.get(row.recommendation_id)}
                            daysWindow={30}
                        />
                    ))}
                </div>
            )}

            {/* Blood Pressure */}
            <BloodPressureTable bp={metrics.blood_pressure_breakdown} />

            {/* HbA1c */}
            <HbA1cTable
                current={metrics.hba1c_breakdown}
                prior={prior?.hba1c_breakdown ?? null}
                currentYear={currentYear}
                priorYear={priorYear}
            />
        </div>
    );
};

export default EmployeeHealthDashboard;
